import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import React from 'react'

import { Location } from '@/components/Views/MapView/types'
import { getLocationsFromLatLng } from '@/components/Views/MapView/utils'

import {
  API_KEY,
  mapOptions as defaultMapOptions,
  markerOptions,
} from './constants'
import {
  useCurrentLocationMarker,
  useFacilityMarker,
  useSelectedFacilityMarker,
} from './hooks'

const iframeContainerStyle = {
  width: '100%',
  height: '100vh',
}

const containerStyle = {
  width: '100%',
  height: '100%',
}

interface MapProps {
  zoom?: number
  iframeMode?: boolean
  center?: { lat: number; lng: number }
  currentLocation?: { lat: number; lng: number } | null
  selectedIds?: Location['id'][]
  locations?: Location[]
  mapOptions?: google.maps.MapOptions
  children?: React.ReactNode
  onMarkerClick?: (location: Location, e: google.maps.MapMouseEvent) => void
  onZoomChanged?: (zoom: number) => void
  onCenterChanged?: (center: google.maps.LatLng) => void
  onIdle?: () => void
  onLoad?: (map: google.maps.Map) => void
  onDragStart?: (center: google.maps.LatLng) => void
  onDragEnd?: (center: google.maps.LatLng) => void
}

const Map = ({
  zoom = 12,
  iframeMode = false,
  center,
  currentLocation,
  selectedIds = [],
  locations = [],
  mapOptions,
  children,
  onMarkerClick,
  onZoomChanged,
  onCenterChanged,
  onIdle,
  onLoad,
  onDragStart,
  onDragEnd,
}: MapProps) => {
  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: API_KEY,
  })

  const [map, setMap] = React.useState<google.maps.Map | null>(null)
  const facilityMarker = useFacilityMarker(isLoaded)
  const selectedFacilityMarker = useSelectedFacilityMarker(isLoaded)
  const currentLocationMarker = useCurrentLocationMarker(isLoaded)

  const handleLoad = React.useCallback(
    (map: google.maps.Map) => {
      setMap(map)
      onLoad?.(map)
    },
    [onLoad],
  )

  const onUnmount = React.useCallback(() => {
    setMap(null)
  }, [])

  const handleCenterChanged = React.useCallback(() => {
    if (!map) return
    if (!onCenterChanged) return

    const c = map.getCenter()
    if (!c) return

    onCenterChanged(c)
  }, [map, onCenterChanged])

  const handleDragStart = React.useCallback(() => {
    if (!map) return
    if (!onDragStart) return

    const c = map.getCenter()
    if (!c) return

    onDragStart(c)
  }, [map, onDragStart])

  const handleIdle = React.useCallback(() => {
    onIdle?.()
  }, [onIdle])

  const handleDragEnd = React.useCallback(() => {
    if (!map) return
    if (!onDragEnd) return

    const c = map.getCenter()
    if (!c) return

    onDragEnd(c)
  }, [map, onDragEnd])

  const handleZoomChanged = React.useCallback(() => {
    if (!map) return
    if (!onZoomChanged) return

    const z = map.getZoom()
    if (!z) return

    onZoomChanged(z)
  }, [map, onZoomChanged])

  const handleMarkerClick = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      if (!onMarkerClick) return

      const [clickedLocation] = getLocationsFromLatLng(locations, e.latLng)
      onMarkerClick(clickedLocation as Location, e)
    },
    [locations, onMarkerClick],
  )

  return isLoaded ? (
    <GoogleMap
      zoom={zoom}
      mapContainerStyle={iframeMode ? iframeContainerStyle : containerStyle}
      center={center}
      options={{ ...defaultMapOptions, ...mapOptions }}
      onLoad={handleLoad}
      onZoomChanged={handleZoomChanged}
      onCenterChanged={handleCenterChanged}
      onUnmount={onUnmount}
      onIdle={handleIdle}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Current location */}
      {currentLocation && (
        <Marker
          position={currentLocation}
          icon={currentLocationMarker}
          options={markerOptions}
        />
      )}

      {/* Other markers */}
      {locations.map((location) => (
        <Marker
          key={location.id}
          icon={
            selectedIds.includes(location.id)
              ? selectedFacilityMarker
              : facilityMarker
          }
          onClick={handleMarkerClick}
          position={location}
          options={markerOptions}
        />
      ))}

      {children}
    </GoogleMap>
  ) : null
}

export default React.memo(Map)
